import React, { useState } from 'react';
import type { ReactElement } from 'react';
import SearchBar from './SearchBar';
import FilterDialog from './components/FilterDialog';
import type {
  RefinementsType,
  SelectedFiltersOptionsType,
} from '../../../shared/model/constants/filterTypes';
import { trackSearchRefinementModalOpen } from '../../../shared/tracking/tealium/tracking';
import type { ChannelObject } from '../../../shared/model/tsTypes';
import {
  DEFAULT_FILTERS_REFINEMENTS_OPTIONS,
  mapFilterOptionsToRefinements,
} from './components/mapRefinementsFilters';
import { urlFromRefinements } from './services/urlFromRefinements';
import history from '../../../client/service/history';
import * as referrerService from '../../../client/tracking/omniture/referrerService';
import { saveRecentSearch } from '../../../client/service/preferenceService';
import type { MapViewPropType } from '../model/query/mapViewOptions';
import { getFirstLocationState, setRecentLocations } from '../../recentSearchesService';
import { setCookie } from '../../../client/cookie/cookieService';

export type FilterRefinementsProps = {
  refinements: RefinementsType;
  initialFilterOptions: SelectedFiltersOptionsType;
  selectedFiltersOptions: SelectedFiltersOptionsType;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  additionalElements: ReactElement[];
  mapView?: MapViewPropType;
};

export const resetPageInRefinements = (refinements: RefinementsType) => ({
  ...refinements,
  page: 1,
});

export const FilterRefinements = ({
  refinements,
  initialFilterOptions,
  selectedFiltersOptions,
  setSelectedFiltersOptions,
  additionalElements,
  mapView,
}: FilterRefinementsProps): ReactElement => {
  const [shouldOpenFilterDialog, setShouldOpenFilterDialog] = useState(false);

  const openFilterDialog = () => {
    trackSearchRefinementModalOpen({
      searchMode: mapView ? 'map' : 'list',
      userAction: 'filters-button',
    });
    setShouldOpenFilterDialog(true);
  };

  const changeUrlOnSearch = (filterOptions: SelectedFiltersOptionsType): void => {
    const filterOptionsToRefinements = mapFilterOptionsToRefinements(filterOptions, refinements);

    const newSearchUrl = urlFromRefinements(resetPageInRefinements(filterOptionsToRefinements));

    saveRecentSearch(newSearchUrl);
    setCookie('ads_params_state', getFirstLocationState(), { expires: 30 });
    referrerService.referAsSRPRefinement();

    history.push(newSearchUrl);
  };

  const executeSearchWithOptions = (filterOptions: SelectedFiltersOptionsType) => {
    setRecentLocations(filterOptions.searchLocations);
    changeUrlOnSearch(filterOptions);
  };

  const handleSearchOnClick = (onClose?: () => void): void => {
    executeSearchWithOptions(selectedFiltersOptions);
    onClose?.();
  };

  const handleChannelChange = (channel: ChannelObject): void => {
    const newChannelFilterOptions: SelectedFiltersOptionsType = {
      ...selectedFiltersOptions,
      ...DEFAULT_FILTERS_REFINEMENTS_OPTIONS,
      channel,
    };
    setSelectedFiltersOptions(newChannelFilterOptions);
    executeSearchWithOptions(newChannelFilterOptions);
  };

  return (
    <>
      <SearchBar
        onFilterOpen={openFilterDialog}
        selectedFiltersOptions={selectedFiltersOptions}
        initialFilterOptions={initialFilterOptions}
        additionalElements={additionalElements}
        setSelectedFiltersOptions={setSelectedFiltersOptions}
        handleSearchOnClick={handleSearchOnClick}
        handleChannelChange={handleChannelChange}
        mapView={mapView}
      />
      {shouldOpenFilterDialog && (
        <FilterDialog
          isOpen={shouldOpenFilterDialog}
          appElement={() => document.getElementById('wrapper') ?? ''}
          onClose={() => {
            setShouldOpenFilterDialog(false);
          }}
          initialFilterOptions={initialFilterOptions}
          selectedFiltersOptions={selectedFiltersOptions}
          setSelectedFiltersOptions={setSelectedFiltersOptions}
          handleSearchOnClick={handleSearchOnClick}
        />
      )}
    </>
  );
};
